'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import dynamic from 'next/dynamic'
import LazyBackgroundImage from './LazyBackgroundImage'
import Image from 'next/image'
import Link from 'next/link'

// Dynamic import with lazy loading optimization
const LazyModel = dynamic(() => import('./LazyModel'), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full animate-pulse items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-bounce rounded-full bg-gray-300"></div>
        <span className="text-gray-500">Preparing 3D Model...</span>
      </div>
    </div>
  ),
})

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function PhotovoltaicSection() {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return null
  }

  // Model paths
  const PANEL_MODEL = '/models/black-photovoltaik.glb'
  const BATTERY_MODEL = '/models/dyness-power-storage.glb'
  const INVERTER_MODEL = '/models/inverter-model.glb'
  const EV_MODEL = '/models/ev-model.glb'

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto">
        <div className="mx-auto">
          <motion.h2
            className="mb-12 text-center text-3xl font-bold md:text-4xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <span className="text-[#ff4500]">Komplettlösungen </span>für Ihre Photovoltaikanlage
          </motion.h2>

          {/* First row: Text left, Model right */}
          <motion.div
            className="mb-24 flex flex-col items-center gap-8 md:flex-row"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="order-2 text-center md:order-1 md:w-1/2 md:text-left">
              <h3 className="mb-4 text-2xl font-bold">
                Hochwertige <span className="text-[#ff4500]">Solarmodule</span> TOPCON N-Type
              </h3>
              <p className="mb-6 text-gray-700">
                Unsere Premium-Solarmodule bieten maximale Effizienz und Langlebigkeit. Mit
                modernster Technologie wandeln sie Sonnenlicht in saubere Energie um und sind
                perfekt auf das deutsche Klima abgestimmt.
              </p>
              <ul className="space-y-2">
                <LazyBackgroundImage
                  src="/images/photovoltaik-image-long.webp"
                  className="rounded-lg bg-cover bg-center p-4 font-bold text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.0 }}
                >
                  Bis zu 25 Jahre Produktgarantie
                </LazyBackgroundImage>
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Hohe Widerstandsfähigkeit gegen Witterungseinflüsse
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Optisch ansprechende Integration in Ihre Dachfläche
                </motion.li>
              </ul>
            </div>
            <div className="order-1 h-80 overflow-hidden md:order-2 md:w-1/2">
              <LazyModel
                modelPath={PANEL_MODEL}
                options={{
                  scale: 2.5,
                  position: [0, 0, 0],
                  rotation: [0, Math.PI / 3, 0],
                  intensity: 1,
                  roughness: 0.1,
                  metalness: 0.7,
                  envMapIntensity: 1.2,
                }}
                rootMargin="200px"
                className="h-full w-full"
              />
            </div>
          </motion.div>

          {/* Second row: Model left, Text right */}
          <motion.div
            className="mb-24 flex flex-col items-center gap-8 md:flex-row"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeIn}
          >
            <div className="h-80 overflow-hidden md:w-1/2">
              <LazyModel
                modelPath={BATTERY_MODEL}
                options={{
                  scale: 6,
                  position: [0, 0, 0],
                  rotation: [0, Math.PI / 3, 0],
                  intensity: 0.8,
                  roughness: 0.8,
                  metalness: 0.2,
                  envMapIntensity: 0.5,
                }}
                rootMargin="200px"
                className="h-full w-full"
              />
            </div>
            <div className="text-center md:w-1/2 md:text-left">
              <h3 className="mb-4 text-2xl font-bold">
                Leistungsstarke <span className="text-[#ff4500]">Dyness</span> Speicher
              </h3>
              <p className="mb-6 text-gray-700">
                Mit unseren Energiespeichern nutzen Sie selbst erzeugten Strom auch dann, wenn die
                Sonne nicht scheint. Die modernen Lithium-Ionen-Batterien bieten höchste Sicherheit
                und eine lange Lebensdauer.
              </p>
              <ul className="space-y-2">
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.0 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700"
                >
                  Optimieren Sie Ihren Eigenverbrauch auf bis zu 85%
                </motion.li>
                <LazyBackgroundImage
                  src="/images/photovoltaik-image-long.webp"
                  className="rounded-lg bg-cover bg-center p-4 font-bold text-white dark:bg-gray-800"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Über 6000 Vollständige Ladezyklen
                </LazyBackgroundImage>
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Modularer Aufbau für flexible Kapazitätserweiterung bis 40.96 kWh
                </motion.li>
              </ul>
            </div>
          </motion.div>

          {/* Third row: Text left, Model right */}
          <motion.div
            className="mb-24 flex flex-col items-center gap-8 md:flex-row"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeIn}
          >
            <div className="order-2 text-center md:order-1 md:w-1/2 md:text-left">
              <h3 className="mb-4 text-2xl font-bold">
                Moderne <span className="text-[#ff4500]">Wechselrichter</span> von Kostal
              </h3>
              <p className="mb-6 text-gray-700">
                Die Dreiphasen-Wechselrichterserie von Kostal ist auf robuste Leistung und
                effizientes Energiemanagement im Wohn- und Gewerbebereich ausgelegt. Mit dem Fokus
                auf hohe Effizienz, nahtlose Integration und erweiterte Schutzfunktionen bieten
                diese Wechselrichter eine vielseitige Lösung für moderne Energiebedürfnisse.
              </p>
              <ul className="space-y-2">
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.0 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Maximale Effizienz von bis zu 97.6%
                </motion.li>
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Integriertes Monitoring über Web und App
                </motion.li>
                <LazyBackgroundImage
                  src="/images/photovoltaik-image-long.webp"
                  className="rounded-lg bg-cover bg-center p-4 font-bold text-white dark:bg-gray-800"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  Leise Betriebsweise für störungsfreien Einsatz
                </LazyBackgroundImage>
              </ul>
            </div>
            <div className="order-1 h-80 overflow-hidden md:order-2 md:w-1/2">
              <LazyModel
                modelPath={INVERTER_MODEL}
                options={{
                  scale: 6,
                  position: [0, 0, 0],
                  rotation: [0, Math.PI / 3, 0],
                  intensity: 3,
                  roughness: 0.3,
                  metalness: 0.9,
                  envMapIntensity: 1.0,
                }}
                rootMargin="200px"
                className="h-full w-full"
              />
            </div>
          </motion.div>

          {/* E.box Section */}
          <div className="mx-auto max-w-[1920px] py-25">
            <div className="mx-auto max-w-7xl">
              <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-12">
                {/* Left Card */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-lg bg-[#ff4500] p-8 text-center text-white md:col-span-4 md:p-12"
                >
                  <div className="items-left flex flex-col justify-center md:gap-4">
                    <h2 className="mb-6 text-2xl font-bold">E.ON Home</h2>
                    <span className="text-md flex flex-col text-left text-white md:gap-4 md:text-lg">
                      <p>✓ Bis zu 480 Euro jährliche Einsparung</p>
                      <p>✓ Energiedaten in Echtzeit einsehen</p>
                      <p>✓ Einfache Installation mit Experten</p>
                    </span>
                  </div>
                </motion.div>

                {/* Right Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-lg bg-[#e9e9e6] py-8 md:col-span-8 md:p-12"
                >
                  <div className="px-4 md:px-0">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-8">
                      {/* Text Content */}
                      <div className="text-center md:col-span-5 md:text-left">
                        <h2 className="mb-6 text-2xl font-bold text-gray-900 sm:text-3xl">
                          E.ON Home <span className="text-[#ff4500]">Energiemanager</span>{' '}
                        </h2>
                        <div className="text-base leading-relaxed text-gray-600 sm:text-lg">
                          <p>
                            <span className="text-[#ff4500]">✓</span> Maximierung der
                            Eigenverbrauchsquote durch solares Laden – bis zu 290 Euro jährlich
                          </p>
                          <p>
                            <span className="text-[#ff4500]">✓</span> Ersparnis durch
                            Netzentgeltreduzierung nach §14a EnWG9 – bis zu 190 Euro jährlich.
                          </p>
                          <p>
                            <span className="text-[#ff4500]">✓</span> Intelligente
                            Heizungssteuerung: Nutzen Sie überschüssigen Solarstrom direkt und
                            efzient für Ihre Wärmepumpe
                          </p>
                        </div>
                      </div>

                      {/* Image */}
                      <div className="flex items-center justify-center md:col-span-3">
                        <div className="relative aspect-[4/4] w-full">
                          <Image
                            src="/images/ebox-section.webp"
                            alt="Premium Solar Panel"
                            fill
                            className="object-contain"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 38vw, 25vw"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Fourth row: Model left, Text right */}
          <motion.div
            className="mb-24 flex flex-col items-center gap-8 md:flex-row"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeIn}
          >
            <h2 className="mb-6 text-2xl font-bold text-gray-900 sm:text-3xl">
              EV-Charger <span className="text-[#ff4500]">Wallbox 11kW</span>
            </h2>
            <div className="h-80 overflow-hidden md:w-1/2">
              <LazyModel
                modelPath={EV_MODEL}
                options={{
                  scale: 6,
                  position: [0, 0, 0],
                  rotation: [0, Math.PI / 3, 0],
                  intensity: 0.8,
                  roughness: 0.8,
                  metalness: 0.2,
                  envMapIntensity: 0.5,
                }}
                rootMargin="200px"
                className="h-full w-full"
              />
            </div>
            <div className="text-center md:w-1/2 md:text-left">
              <p className="mb-6 text-gray-700">
                Mit der Sungrow Wallbox laden Sie Ihr E-Auto sicher und effizient – auch wenn keine
                Sonne scheint. Die robuste Technik sorgt für zuverlässige Leistung und einfache
                Integration.
              </p>
              <ul className="space-y-2">
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.0 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700"
                >
                  Liefert bis zu 11 kW Ladeleistung.
                </motion.li>
                <LazyBackgroundImage
                  src="/images/photovoltaik-image-long.webp"
                  className="rounded-lg bg-cover bg-center p-4 font-bold text-white dark:bg-gray-800"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Schutzart IP65, für Außeninstallationen.
                </LazyBackgroundImage>
                <motion.li
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="rounded-lg bg-[#e9e9e6] p-4 text-gray-700 dark:bg-gray-800"
                >
                  Verfügt über adaptives Lastmanagement, um Überlastungen zu vermeiden.
                </motion.li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
